import React from 'react'
import { useState, useEffect, Link } from 'react'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Pagination from '../components/Pagination'
import { faMagnifyingGlass, faEye,  faSortDown,
  faSortUp,
  faSort, } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'

function ClinicClients({ clinicId }) {
  // Auth
  const { can, createInfo } = useAuth()
  const axios = useAxiosPrivate()

  // Service
  const [clients, setClients] = useState([])
  const [currentRecords, setCurrentRecords] = useState([])

  // search
  const [filteredRecords, setFilteredRecords] = useState([])
  const [nPages, setNumberOfPages] = useState(0)

  const [currentPage, setCurrentPage] = useState(1)
  // No of Records to be displayed on each page
  const [recordsPerPage] = useState(10)

  const [indexOfLastRecord, setIndexOfLastRecord] = useState(10)
  const [indexOfFirstRecord, setIndexOfFirstRecord] = useState(0)

  const [originalRecords, setOriginalRecords] = useState([]);
  const [columnClickCounts, setColumnClickCounts] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending',
  });
  
  const [risks, setRisks] = useState([])
  const [riskFilter, setRiskFilter] = useState('')
  const [searchFilteredRecordIds, setSearchFilteredRecordIds] = useState([])
  const [riskFilteredRecordIds, setRiskFilteredRecordIds] = useState([])
  const [searchFilter, setSearchFilter] = useState('')

  const navigate = useNavigate()

  useEffect(() => {
    if (clinicId) {
      getClients()
      fetchRisks()
    }
  }, [clinicId])

  const viewClient = (clientId) => {
    navigate(`/clients/${clientId}`)
  }

  const handleSearch = (handle) => {
    let searchValue = handle.target.value
    let searchString = searchValue.trim().toLowerCase()
    if (searchString.length > 0) {
      let records = clients.filter((e) => {
        return (
          e.name?.toLowerCase().match(searchString) ||
          e.email?.toLowerCase().match(searchString) ||
          e.reference?.toLowerCase().match(searchString)
        )
      })
      let ids = []
      records.forEach((record) => {
        ids.push(record.id)
      })
      setSearchFilter(searchString)
      setSearchFilteredRecordIds(ids)
    } else {
      setSearchFilter('')
    }
  }

  useEffect(() => {
    setCurrentPage(1)
    let curRecords = []
    if (!searchFilter && !riskFilter) {
      curRecords = clients
    } else {
      curRecords = clients.filter((record) => {
        return (
          (!riskFilter || riskFilteredRecordIds.includes(record.id)) && 
          (!searchFilter || searchFilteredRecordIds.includes(record.id)))
      })
    }

    setFilteredRecords(curRecords)
    setCurrentRecords(curRecords.slice(0, 10))
    setIndexOfFirstRecord(0)
    setIndexOfLastRecord((recordsPerPage > curRecords.length) ? curRecords.length : recordsPerPage)
    setNumberOfPages(Math.ceil(curRecords.length / recordsPerPage))
  }, [searchFilter, riskFilter])

  const sortArray = (array, key, direction) => {
    return [...array].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const renderSortArrow = (columnKey) => {
    if (sortConfig.key === columnKey) {
      if (sortConfig.direction === 'ascending') {
        return <FontAwesomeIcon icon={faSortUp} />;
      } else {
        return <FontAwesomeIcon icon={faSortDown} />;
      }
    }
    return <FontAwesomeIcon icon={faSort} />;
  };

  const handleSort = (key) => {
    let newColumnClickCounts = { ...columnClickCounts };
    if (!newColumnClickCounts[key]) {
      newColumnClickCounts[key] = 1;
    } else {
      newColumnClickCounts[key]++;
    }
  
    if (newColumnClickCounts[key] === 3) {
      setSortConfig({
        key: null,
        direction: 'descending',
      });
      newColumnClickCounts[key] = 0;
      setCurrentRecords(originalRecords.slice(
        indexOfFirstRecord,
        indexOfLastRecord
      ));
    } else {
      let direction =
        sortConfig.direction === 'ascending'
          ? 'descending'
          : 'ascending';
      setSortConfig({ key, direction });
  
      const sortedRecords = sortArray(
        originalRecords,
        key,
        direction
      );
      setCurrentRecords(sortedRecords.slice(
        indexOfFirstRecord,
        indexOfLastRecord
      ));
    }
    setColumnClickCounts(newColumnClickCounts);
  };

  const nextPage = () => {
    if (currentPage < nPages) {
      const newPage = currentPage + 1;
      const indexFirst = newPage * recordsPerPage - recordsPerPage;
      const indexLast = (newPage * recordsPerPage > filteredRecords.length) ? filteredRecords.length : newPage * recordsPerPage;
  
      setIndexOfFirstRecord(indexFirst);
      setIndexOfLastRecord(indexLast);
      setCurrentPage(newPage);
      const sortedRecords = sortArray(originalRecords, sortConfig.key, sortConfig.direction);
      setCurrentRecords(sortedRecords.slice(indexFirst, indexLast));
    }
  };
  
  const previousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      const indexFirst = (newPage - 1) * recordsPerPage;
      const indexLast = newPage * recordsPerPage;
  
      setIndexOfFirstRecord(indexFirst);
      setIndexOfLastRecord(indexLast);
      setCurrentPage(newPage);
  
      const sortedRecords = sortArray(originalRecords, sortConfig.key, sortConfig.direction);
      setCurrentRecords(sortedRecords.slice(indexFirst, indexLast));
    }
  };

  const fetchRisks = async () => {
    try {
      const response = await axios.get('/risk-options/all');
      const risks = response.data.result;
      setRisks(risks);
    } catch (error) {
      createInfo('Error fetching risks');
    }
  };

  const getClients = async () => {
    try {
      const response = await axios.get(`/companies/clients/` + clinicId)
      if (response.data.result) {
        await setClients(response.data.result)
        await setFilteredRecords(response.data.result)
        await setOriginalRecords(response.data.result);
        
        await setIndexOfLastRecord(response.data.result?.length < 10 ? response.data.result?.length : 10)

        await setCurrentRecords(response.data.result.slice(
          indexOfFirstRecord,
          indexOfLastRecord
        ))
        await setNumberOfPages(
          Math.ceil(response.data.result.length / recordsPerPage)
        );
      }
    } catch (error) {}
  }

  const handleRiskChange = (e) => {
    const value = e.target.value
    setRiskFilter(value);
    
    const riskFilteredRecords = clients.filter((record) => {
      return record.risk[0]?.id?.toString() === value
    })

    let ids = []
    riskFilteredRecords.forEach((record) => {
      ids.push(record.id)
    })

    setRiskFilteredRecordIds(ids)
  }

  const resetFilters = () => {
    setRiskFilter('');
    setSearchFilter('')
    setFilteredRecords(clients)
    setCurrentRecords(clients.slice(indexOfFirstRecord, indexOfLastRecord))
    setNumberOfPages(Math.ceil(clients.length / recordsPerPage))
  };

  const isFilterApplied = () => {
    return riskFilter !== '' || searchFilter !== '';
  };

  return (
    <section className="w-full">
      {can('view clinic clients') && (
        <div>
          <div className="flex items-center w-full justify-between"></div>
          <div className="pb-4 flex justify-between items-center">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FontAwesomeIcon
                  icon={faMagnifyingGlass}
                  onClick={handleSearch}
                />
              </div>
              <input
                type="text"
                id="table-search"
                onChange={handleSearch}
                value={searchFilter}
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-md focus:ring-blue-500 focus:border-blue-500 block w-80 pl-10 p-2.5  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                placeholder="Search Clients"
              />
            </div>

            <div className="py-4">
              <select
                onChange={handleRiskChange}
                value={riskFilter}
                className='px-4 py-2 border border-gray-300 rounded-md shadow-md mr-2'
              >
                
                <option value="">Filter by Risk</option>
                {risks.map((risk, index) => (
                  <option key={risk.id} value={risk.id}>
                    {risk.title}
                  </option>
                ))}
              </select>
              {isFilterApplied() && (
                <button
                  onClick={resetFilters}
                  className="bg-red-500 text-white rounded-lg px-4 py-2 ml-4"
                >
                  Reset Filters
                </button>
              )}
            </div>
          </div>

          <table className="table-main">
            <thead>
              <tr>
                <th className='cursor-pointer' scope="col" onClick={() => handleSort('name')}>Name {renderSortArrow('name')}</th>
                <th className='cursor-pointer' scope="col" onClick={() => handleSort('email')}>Email {renderSortArrow('email')}</th>
                {can('view client status') && <th className='cursor-pointer' scope="col" onClick={() => handleSort('status')}>Status {renderSortArrow('status')} </th>}
                <th className='cursor-pointer' scope="col" onClick={() => handleSort('reference')}>Reference {renderSortArrow('reference')}</th>
                <th className='cursor-pointer' scope="col" onClick={() => handleSort('created_at')}>Created At {renderSortArrow('created_at')}</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.length > 0 ? (
                currentRecords?.map((user) => {
                  return (
                    <tr key={user.id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      {can('view client status') && (
                        <td>
                          <div
                            className={`status ${user.status.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            {user.status}
                          </div>
                        </td>
                      )}
                      <td>{user.reference}</td>
                      <td>{moment(user.created_at).format('DD/MM/YYYY')}</td>
                      {can('view clinic clients') && (
                        <td>
                          <div className="flex justify-center">
                          <span
                          title="View Client"
                          onClick={() => viewClient(user.id)}
                          className="flex justify-center items-center bg-blue-400 rounded-md text-blue-800 h-9 w-12 mx-2  hover:bg-blue-500 cursor-pointer"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </span>
                    </div>
                        </td>
                      )}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={6}>
                    <p className="no-records">No clients found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Pagination
            next={nextPage}
            prev={previousPage}
            first={indexOfFirstRecord}
            last={indexOfLastRecord}
            total={filteredRecords.length}
          />
        </div>
      )}
    </section>
  )
}

export default ClinicClients
